<script lang="ts" setup>
import { ElNotification } from "element-plus";
import { onMounted, ref } from "vue";
import { UserWithPassword } from "~/types";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import { Hierarchy } from "~/types/enums/positionalHierarchy";
import { useConfigStore } from "~/stores/configStore";
import { PermissionNames } from "~/configs/permission_names";
import { useBranchStore } from "~/stores/branchesStore";
import { usePartnerAgents } from "~/composable/usePartnerAgents";

const props = defineProps({
  showEditModalHeader: {
    type: Boolean,
    default: true,
  },
});

const configStore = useConfigStore();
const usersStore = useUsersStore();
const branchStore = useBranchStore();

// One agent dropdown per configured delivery partner that maps agents, built
// from every integration of that partner rather than just the first.
const { load: loadPartnerAgents, partnerAgentFields } = usePartnerAgents();

onMounted(async () => {
  await usersStore.getAllRoles();
  await branchStore.getBranchCollection();
  await loadPartnerAgents();
});

const hierarchy = ref(Hierarchy.ORGANIZATIONAL_ADMIN);
const formRef = ref<ElementUIFormRef | null>(null);

const userForm = reactive<Partial<UserWithPassword>>(
  {} as Partial<UserWithPassword>,
);

const autofillUsername = (name) => {
  userForm.user_name = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_");
};

const validateEmail = async function (
  rule: FormRuleType,
  value: string,
  callback: (arg0?: Error) => void,
) {
  if (value !== "") {
    const exists = await usersStore.emailExists(value);
    if (exists) {
      return callback(new Error("Email already exists"));
    }
  }
  callback();
};

const validateUserName = async function (
  rule: FormRuleType,
  value: string,
  callback: (arg0?: Error) => void,
) {
  if (value !== "") {
    if (value.includes("trunk")) {
      return callback(new Error("User name cannot contain 'trunk'"));
    }
    const exists = await usersStore.userNameExists(value);
    if (exists) {
      return callback(new Error("User name already exists"));
    }
  }
  callback();
};

const validatePassword = function (
  rule: FormRuleType,
  value: string,
  callback: () => void,
) {
  if (userForm.password_confirmation !== "") {
    // @ts-ignore
    formRef.value.validateField("user.password_confirmation");
  }
  callback();
};

const validateConfirmPassword = function (
  rule: FormRuleType,
  value: string,
  callback: (arg0?: Error) => void,
) {
  if (value !== "" && value !== userForm.password) {
    callback(new Error("Passwords does not match"));
  }
  callback();
};

const rules = reactive<FormRuleType>({
  name: [
    {
      required: true,
      message: "Name is required",
      trigger: "blur",
    },
  ],
  email: [
    {
      type: "email",
      required: true,
      message: "Email must be a valid email",
      trigger: "blur",
    },
    {
      asyncValidator: validateEmail,
      message: "Email already exists",
      trigger: "blur",
    },
  ],
  user_name: [
    {
      type: "pattern",
      pattern: /^[a-z0-9_]+$/,
      required: true,
      message: "User name must be lowercase, digits and underscore",
      trigger: "blur",
    },
    {
      asyncValidator: validateUserName,
      trigger: "blur",
    },
  ],
  password: [
    {
      required: true,
      message: "Password is required",
      trigger: "blur",
    },
    {
      validator: validatePassword,
      message: "Passwords does not match",
      trigger: "change",
    },
    { min: 6, message: "Password length should be at least 6" },
  ],
  password_confirmation: [
    {
      required: true,
      message: "Confirm password is required",
      trigger: "blur",
    },
    {
      validator: validateConfirmPassword,
      message: "Passwords does not match",
      trigger: "change",
    },
    { min: 6, message: "Password length should be at least 6" },
  ],
  roles: [
    {
      required: true,
      message: "Roles is required",
      trigger: "blur",
    },
  ],
  mobile: [
    {
      required: true,
      message: "Mobile is required",
      trigger: "blur",
    },
  ],
  hierarchical_position: [
    {
      required: true,
      message: "User type is required",
      trigger: "blur",
    },
  ],
  branch_id: [
    {
      required: true,
      message: "Branch is required",
      trigger: "blur",
    },
  ],
});

userForm.dept_head_ids = userForm.manages_heads?.map((h) => h.id) ?? [];
userForm.team_lead_ids = userForm.manages_team_leads?.map((tl) => tl.id) ?? [];
userForm.lead_department_ids =
  userForm.manages_departments?.map((d) => d.id) ?? [];

const multipartFormData = new FormData();
const fileInputRef = ref();

const handleFileChange = (event: any) => {
  const file = event.target.files[0];
  multipartFormData.set("profile_picture", file);
};

const createUser = function () {
  if (!formRef.value) return;
  formRef.value?.validate(async (valid: boolean) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all the required fields",
        type: "error",
      });
      return;
    }
    userForm.hierarchical_position = hierarchy.value;

    for (const [key, value] of Object.entries(userForm)) {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          multipartFormData.append(`${key}[]`, item);
        });
      } else if (key !== "profile_picture" && value != null) {
        multipartFormData.set(key, value);
      }
    }
    const user = await usersStore.createUser(multipartFormData);
    if (user.id) {
      ElNotification({
        title: "User created",
        message: "New user created",
        type: "success",
      });
      await usersStore.getUsers();
      await configStore.getConfig();
      usersStore.closeUsersCreateModal();
    }
  });
};

const getLabelByKey = (key: string): string => {
  const option =
    configStore.globalConfig.organization_settings.org_hierarchical_position_names.find(
      (item) => item.key === key,
    );
  return option ? option.label : key;
};
</script>
<template>
  <EditModalHeader v-if="props.showEditModalHeader">
    <template #title> Create User</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="userForm as UserWithPassword"
    :rules="rules as FormRuleType"
    class="h-full"
    @submit.prevent="createUser"
  >
    <div class="feature-filter-grid-1">
      <el-form-item class="form-elem-container" prop="name">
        <div class="form-elem-container">
          <FormLabel :required="true">Name</FormLabel>
          <el-input v-model="userForm.name" @input="autofillUsername" />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="user_name">
        <div class="form-elem-container">
          <FormLabel :required="true"> System username</FormLabel>
          <el-input v-model="userForm.user_name" />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="email">
        <div class="form-elem-container">
          <FormLabel :required="true"> Email</FormLabel>
          <el-input v-model="userForm.email" type="email" />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="password">
        <div class="form-elem-container">
          <FormLabel :required="true"> Password</FormLabel>
          <el-input v-model="userForm.password" type="password" />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="password_confirmation">
        <div class="form-elem-container">
          <FormLabel :required="true"> Confirm Password</FormLabel>
          <el-input v-model="userForm.password_confirmation" type="password" />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="roles">
        <div class="form-elem-container">
          <FormLabel :required="true">Roles</FormLabel>
          <el-select v-model="userForm.roles" multiple>
            <el-option
              v-for="role in usersStore.allRoles"
              :key="role.id"
              :label="role.name"
              :value="role.id"
            ></el-option>
          </el-select>
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="mobile">
        <div class="form-elem-container">
          <FormLabel :required="true"> Mobile Phone</FormLabel>
          <el-input v-model="userForm.mobile" type="number" />
        </div>
      </el-form-item>

      <el-form-item class="form-elem-container" prop="branch_id">
        <div class="form-elem-container">
          <FormLabel :required="true">Branch</FormLabel>
          <el-select v-model="userForm.branch_id" filterable clearable>
            <el-option
              v-for="branch in branchStore._branches"
              :key="branch.id"
              :label="branch.name"
              :value="branch.id"
            ></el-option>
          </el-select>
        </div>
      </el-form-item>

      <!-- One dropdown per configured delivery partner that maps agents. A
           partner with several integrations lists the agents from all of
           them, named by integration so duplicates are tellable apart. -->
      <el-form-item
        v-for="partner in partnerAgentFields"
        :key="partner.key"
        class="form-elem-container"
        :prop="partner.field"
      >
        <div class="form-elem-container">
          <FormLabel :required="false">{{ partner.label }}</FormLabel>
          <el-select
            v-model="userForm[partner.field]"
            filterable
            clearable
            :disabled="!partner.agents.length"
            :placeholder="partner.placeholder"
          >
            <el-option
              v-for="agent in partner.agents"
              :key="`${agent.integrationId}:${agent.value}`"
              :label="agent.label"
              :value="agent.value"
            >
              <span>{{ agent.label }}</span>
              <span v-if="agent.hint" class="text-gray-400 text-xs ml-2">
                ({{ agent.hint }})
              </span>
              <span
                v-if="partner.showIntegrationName"
                class="text-gray-400 text-xs ml-2"
              >
                · {{ agent.integrationName }}
              </span>
            </el-option>
          </el-select>
          <p v-if="partner.emptyReason" class="text-xs text-amber-600 mt-1">
            {{ partner.emptyReason }}
          </p>
        </div>
      </el-form-item>

      <template v-if="configStore.isOrgStructureHierarchical">
        <div class="form-el-container">
          <FormLabel :required="true">Select User Type</FormLabel>
        </div>
        <el-radio-group v-model="hierarchy" class="form-elem-container">
          <div class="flex flex-col gap-1">
            <el-radio :label="Hierarchy.ORGANIZATIONAL_ADMIN">{{
              getLabelByKey(Hierarchy.ORGANIZATIONAL_ADMIN)
            }}</el-radio>
            <el-radio :label="Hierarchy.HEAD">{{
              getLabelByKey(Hierarchy.HEAD)
            }}</el-radio>
            <el-radio
              v-if="
                configStore.globalConfig.organization_settings
                  .org_hierarchy_levels === 4
              "
              :label="Hierarchy.TEAM_LEAD"
              >{{ getLabelByKey(Hierarchy.TEAM_LEAD) }}</el-radio
            >
            <el-radio :label="Hierarchy.MEMBER">{{
              getLabelByKey(Hierarchy.MEMBER)
            }}</el-radio>
          </div>
        </el-radio-group>

        <!-- org admin -->
        <el-form-item
          v-if="hierarchy === Hierarchy.ORGANIZATIONAL_ADMIN"
          class="form-elem-container"
          prop="dept_head_ids"
        >
          <div class="form-elem-container">
            <FormLabel>{{ getLabelByKey(Hierarchy.HEAD) }} (Manages)</FormLabel>
            <el-select
              v-model="userForm.dept_head_ids"
              multiple
              filterable
              clearable
            >
              <el-option
                v-for="item in configStore.getUsersByHierarchy(Hierarchy.HEAD)"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </div>
        </el-form-item>

        <!-- head -->
        <el-form-item
          v-if="
            hierarchy === Hierarchy.HEAD &&
            configStore.globalConfig.organization_settings
              .org_hierarchy_levels === 4
          "
          class="form-elem-container"
          prop="team_lead_ids"
        >
          <div class="form-elem-container">
            <FormLabel
              >{{ getLabelByKey(Hierarchy.TEAM_LEAD) }} (Manages)</FormLabel
            >
            <el-select
              v-model="userForm.team_lead_ids"
              filterable
              multiple
              clearable
              @change="userForm.lead_department_ids = []"
            >
              <el-option
                v-for="item in configStore.getUsersByHierarchy(
                  Hierarchy.TEAM_LEAD,
                )"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </div>
        </el-form-item>

        <el-form-item
          v-if="
            hierarchy === Hierarchy.HEAD &&
            configStore.globalConfig.organization_settings
              .org_hierarchy_levels === 3
          "
          class="form-elem-container"
          prop="lead_department_ids"
        >
          <div class="form-elem-container">
            <FormLabel>Departments (Manages)</FormLabel>
            <el-select
              v-model="userForm.lead_department_ids"
              filterable
              multiple
              clearable
              @change="userForm.team_lead_ids = []"
            >
              <el-option
                v-for="item in configStore.globalConfig.departments"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </div>
        </el-form-item>

        <!-- team lead -->
        <el-form-item
          v-if="hierarchy === Hierarchy.TEAM_LEAD"
          class="form-elem-container"
          prop="lead_department_ids"
        >
          <div class="form-elem-container">
            <FormLabel>Departments (Manages)</FormLabel>
            <el-select
              v-model="userForm.lead_department_ids"
              filterable
              multiple
              clearable
            >
              <el-option
                v-for="item in configStore.globalConfig.departments"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </div>
        </el-form-item>

        <!-- member -->
        <el-form-item
          v-if="hierarchy === Hierarchy.MEMBER"
          class="form-elem-container"
          prop="department_id"
        >
          <div class="form-elem-container">
            <FormLabel>Department</FormLabel>
            <el-select v-model="userForm.department_id" filterable clearable>
              <el-option
                v-for="item in configStore.globalConfig.departments"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </div>
        </el-form-item>
      </template>
      <el-form-item class="form-elem-container" prop="profile_picture">
        <div class="form-elem-container">
          <FormLabel>Profile Picture</FormLabel>
          <input
            ref="fileInputRef"
            type="file"
            name="file"
            accept=".jpg, .jpeg, .png"
            @change="handleFileChange"
          />
        </div>
      </el-form-item>
    </div>

    <div class="feature-filter-submit">
      <NavButton @click="usersStore.closeUsersCreateModal()"> Cancel</NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.USER_CREATE)"
      >
        Create
      </NavButton>
    </div>
  </el-form>
</template>
